/**
 * Helpers for the floor-plan SVG returned by trusstor's storage endpoint.
 *
 * Mirrors the conventions in `trusstor_dashboard/shared/utils/svg.utils.ts`:
 *
 *   - Each area polygon (or the group wrapping it) carries an SVG `id` like
 *     `area<areaId>` or `f<floorId>area<areaId>`. Sometimes export tools
 *     append a `_<suffix>` (e.g. `area101_3`); we strip it on mount so the
 *     ids are stable and queryable.
 *
 *   - `getAreaIdFromElementId` extracts the bare areaId so we can
 *     cross-reference a tapped element with the project's areas list.
 *
 *   - `paintSelectedAreas` toggles the base selection tint on each area polygon
 *     based on whether its id is in the current selection set; `highlightActiveArea`
 *     overlays the darker "active" tint on the hovered area (and reverts the
 *     previous one); `findAreaElements` / `unionCenterPercent` locate an area's
 *     polygons and their combined centre for placing its name label.
 */

import { cleanAreaElementId, getAreaIdFromElementId } from '@d-orca/shared/client/issues';

export { getAreaIdFromElementId };

const AREA_ID_SELECTOR = '[id*="area"]';

const HIGHLIGHT_PROPERTIES = [
  'opacity',
  'fill-opacity',
  'fill',
  'stroke',
  'stroke-width',
  'mix-blend-mode',
] as const;

/** The closed set of inline CSS props applied to an area polygon. */
export type AreaStyle = Partial<Record<(typeof HIGHLIGHT_PROPERTIES)[number], string>>;

/**
 * Selected-area highlight pulled from design tokens (`--floor-area-*` in
 * `tokens.css`) so colour values stay co-located with the rest of the
 * palette per the project's tokens-only rule. Resolved at first call and
 * memoised — token values are static at the document level. The rendering
 * matches trusstor_dashboard's reference verbatim:
 *   `opacity: 0.8; fill-opacity: 0.6; fill: rgba(104, 228, 255, 0.3)`
 */
let cachedStyle: AreaStyle | null = null;
function resolveSelectedAreaStyle(): AreaStyle {
  if (cachedStyle) return cachedStyle;
  const cs = getComputedStyle(document.documentElement);
  const get = (name: string, fallback: string): string =>
    cs.getPropertyValue(name).trim() || fallback;
  cachedStyle = {
    opacity: get('--floor-area-selected-opacity', '0.8'),
    'fill-opacity': get('--floor-area-selected-fill-opacity', '0.6'),
    fill: get('--floor-area-selected-fill', 'rgba(104, 228, 255, 0.3)'),
  };
  return cachedStyle;
}

/**
 * Highlight style for the hovered/active area — the selected tint with a darker
 * shade of the same blue (no new hue). Overlaid on the base tint by
 * `highlightActiveArea`, which reverts it when the area stops being active (or
 * it's wiped wholesale by the next `paintSelectedAreas` repaint).
 */
let cachedActiveStyle: AreaStyle | null = null;
function resolveActiveAreaStyle(): AreaStyle {
  if (cachedActiveStyle) return cachedActiveStyle;
  const fill = getComputedStyle(document.documentElement)
    .getPropertyValue('--floor-area-active-fill')
    .trim();
  cachedActiveStyle = { ...resolveSelectedAreaStyle(), fill: fill || 'rgba(46, 191, 253, 0.42)' };
  return cachedActiveStyle;
}

/** Strips `_<suffix>` from area-id elements so subsequent lookups are stable. */
export function cleanSvgAreaIds(root: Element): void {
  root.querySelectorAll(AREA_ID_SELECTOR).forEach((el) => {
    el.id = cleanAreaElementId(el.id);
  });
}

/**
 * Walks up from `start` until an ancestor's id contains `area`. Click events
 * inside an SVG often fire on the inner `<path>` rather than the wrapping
 * `<g id="area101">`, so a single `event.target` lookup misses the area.
 */
export function findAreaElement(start: Element | null): Element | null {
  let cur: Element | null = start;
  while (cur && (!cur.id || !cur.id.includes('area'))) {
    cur = cur.parentElement;
  }
  return cur;
}

/** Every SVG element that belongs to `areaId` (one area can span several). */
export function findAreaElements(root: Element, areaId: string): SVGElement[] {
  return Array.from(root.querySelectorAll<SVGElement>(AREA_ID_SELECTOR)).filter(
    (el) => getAreaIdFromElementId(el.id) === areaId,
  );
}

/** Imperatively set an area's highlight properties (SVG is foreign markup). */
function applyAreaStyle(el: SVGElement, style: Readonly<AreaStyle>): void {
  for (const [prop, value] of Object.entries(style)) {
    if (value != null) el.style.setProperty(prop, value);
  }
}

/**
 * Applies the highlight style to every area polygon whose id is in
 * `selectedAreaIds`, and clears the style on every other area. Imperative
 * style mutation is the right approach here — the SVG is foreign markup we
 * don't own, so we can't just bind class names to it via JSX.
 */
export function paintSelectedAreas(
  root: Element,
  selectedAreaIds: ReadonlySet<string>,
  style: Readonly<AreaStyle> = resolveSelectedAreaStyle(),
): void {
  root.querySelectorAll<SVGElement>(AREA_ID_SELECTOR).forEach((el) => {
    const areaId = getAreaIdFromElementId(el.id);
    if (selectedAreaIds.has(areaId)) {
      applyAreaStyle(el, style);
    } else {
      HIGHLIGHT_PROPERTIES.forEach((prop) => el.style.removeProperty(prop));
    }
  });
}

/**
 * Moves the "active" darken from `previousAreaId` to `activeAreaId`: reverts
 * every polygon of the previous area to the base selected tint and darkens
 * every polygon of the new one. `applyAreaStyle` only *overlays* the darker
 * fill — it never removes it — so leaving an area relies on this reset. Touches
 * only those two areas, so it's cheap to call at hover cadence. Returns the new
 * active area's elements so the caller can reuse them (e.g. to place a label).
 */
export function highlightActiveArea(
  root: Element,
  activeAreaId: string | null,
  previousAreaId: string | null,
  selectedAreaIds: ReadonlySet<string>,
): SVGElement[] {
  if (previousAreaId && previousAreaId !== activeAreaId && selectedAreaIds.has(previousAreaId)) {
    findAreaElements(root, previousAreaId).forEach((el) => applyAreaStyle(el, resolveSelectedAreaStyle()));
  }
  if (!activeAreaId || !selectedAreaIds.has(activeAreaId)) return [];
  const activeElements = findAreaElements(root, activeAreaId);
  activeElements.forEach((el) => applyAreaStyle(el, resolveActiveAreaStyle()));
  return activeElements;
}

/**
 * Centre of the union of `elements`' rendered boxes, expressed as a percentage
 * of `container`. Null when there's nothing to place or the container has no
 * size — pure enough to unit-test with stubbed rects.
 */
export function unionCenterPercent(
  elements: readonly Pick<Element, 'getBoundingClientRect'>[],
  container: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
): { xPercentage: number; yPercentage: number } | null {
  if (elements.length === 0 || container.width === 0 || container.height === 0) return null;
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const element of elements) {
    const rect = element.getBoundingClientRect();
    left = Math.min(left, rect.left);
    top = Math.min(top, rect.top);
    right = Math.max(right, rect.right);
    bottom = Math.max(bottom, rect.bottom);
  }
  return {
    xPercentage: (((left + right) / 2 - container.left) / container.width) * 100,
    yPercentage: (((top + bottom) / 2 - container.top) / container.height) * 100,
  };
}

